import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { getRoleLabel } from '../../lib/utils';
import { Plus, MoreVertical, Edit, Key, Shield, Users, ShieldCheck, ShieldOff, Lock, Mail } from 'lucide-react';

export function UsersTable({
  users,
  onCreateClick,
  onEditClick,
  onPermissionsClick,
  onResetPassword,
  onResetMFA,
  onToggleStatus,
  onSendCredentials,
}) {
  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium mb-2">No users found</h3>
          <Button onClick={onCreateClick}>
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Name</th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Role</th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Permissions</th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">MFA</th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-muted/30" data-testid={`user-row-${user.id}`}>
                  <td className="px-4 py-3 font-medium">{user.name}</td>
                  <td className="px-4 py-3 text-sm">{user.email}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{getRoleLabel(user.role)}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    {user.permissions && Object.keys(user.permissions).filter(k => user.permissions[k]).length > 0 ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => onPermissionsClick(user)}
                      >
                        <Lock className="w-3 h-3 mr-1" />
                        {Object.keys(user.permissions).filter(k => user.permissions[k]).length} permissions
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-muted-foreground"
                        onClick={() => onPermissionsClick(user)}
                      >
                        Set permissions
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {user.mfa_enabled ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        Enabled
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        <ShieldOff className="w-3 h-3 mr-1" />
                        Disabled
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {user.is_active ? (
                      <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                    ) : (
                      <Badge className="bg-zinc-100 text-zinc-500">Disabled</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEditClick(user)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit User
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onResetPassword(user)}>
                          <Key className="w-4 h-4 mr-2" />
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onSendCredentials(user)} data-testid={`btn-send-credentials-${user.id}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          Send Credentials
                        </DropdownMenuItem>
                        {user.mfa_enabled && (
                          <DropdownMenuItem onClick={() => onResetMFA(user)}>
                            <Shield className="w-4 h-4 mr-2" />
                            Reset MFA
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => onToggleStatus(user)}>
                          {user.is_active ? 'Disable User' : 'Enable User'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
